/**
 * CustomWebsocketProvider - WebSocket provider for custom Lambda Yjs sync
 *
 * Unlike y-websocket's WebsocketProvider which uses binary protocol,
 * this provider communicates with our Lambda using JSON messages:
 *
 * Client -> Server:
 *   { action: 'subscribe', roomId: string }
 *   { action: 'update', roomId: string, update: base64 }
 *   { action: 'awareness', roomId: string, awareness: object }
 *   { action: 'ping' }
 *
 * Server -> Client:
 *   { action: 'sync', type: 'initial', roomId: string, state: base64 | null }
 *   { action: 'sync', type: 'update', roomId: string, update: base64 }
 *   { action: 'awareness', roomId: string, data: object }
 *   { action: 'pong' }
 */

import * as Y from 'yjs';
import { Observable } from 'lib0/observable';
import * as encoding from 'lib0/encoding';
import * as decoding from 'lib0/decoding';
import { API } from 'aws-amplify';
import { migratedUpdateIds, pruneMigratedEntries } from 'src/utils/legacyQueueMigration';

// Telemetry schema version and frozen event types (Tightening #7)
const TELEMETRY_SCHEMA_VERSION = 'v2';

const TELEMETRY_EVENT_TYPES = {
  // Core update lifecycle
  UPDATE_PROCESSED: 'yjs_update_processed',      // Legacy, keep for continuity
  UPDATE_QUEUED: 'yjs_update_queued',
  UPDATE_SENT: 'yjs_update_sent',
  UPDATE_APPLIED: 'yjs_update_applied',          // Remote update applied to Y.Doc
  UPDATE_ACKED: 'yjs_update_acked',
  UPDATE_DISCARDED: 'yjs_update_discarded',
  PENDING_UPDATE_DISCARDED: 'yjs_pending_update_discarded',  // Batch discard event

  // ACK reliability
  ACK_TIMEOUT: 'yjs_ack_timeout',
  RETRY_EXHAUSTED: 'yjs_retry_exhausted',
  EXHAUSTED_RETRY_ATTEMPT: 'yjs_exhausted_retry_attempt',  // Retry after exhaustion

  // Queue management
  QUEUE_LIMIT_EVICTION: 'yjs_queue_limit_eviction',
  QUEUE_LIMIT_BLOCKED: 'yjs_queue_limit_blocked',    // Queue full of user-edits
  USER_EDIT_BLOCKED: 'yjs_user_edit_blocked',        // User edit rejected

  // Comment tracking (Phase 2)
  COMMENT_FIELD_CHANGED: 'yjs_comment_field_changed',

  // Connection lifecycle
  CONNECT: 'yjs_connect',
  DISCONNECT: 'yjs_disconnect',
  SYNC_START: 'yjs_sync_start',
  SYNC_COMPLETE: 'yjs_sync_complete',

  // Telemetry reliability (Phase 1)
  TELEMETRY_DROPPED: 'yjs_telemetry_dropped',
  TELEMETRY_QUEUED: 'yjs_telemetry_queued',
  TELEMETRY_SENT: 'yjs_telemetry_sent',

  // Legacy/compatibility events
  UPDATE_APPLIED_REMOTE: 'yjs_update_applied_remote',
  UPDATE_SEND_FAILED: 'yjs_update_send_failed',
  UPDATE_REPLAYED: 'yjs_update_replayed',
  UPDATE_REPLAY_FAILED: 'yjs_update_replay_failed',
  UPDATE_REPLAY_BLOCKED: 'yjs_update_replay_blocked',
  REPLAY_COMPLETE: 'yjs_replay_complete',
  REPLAY_PARTIAL: 'yjs_replay_partial',
  UNACKED_REQUEUED: 'yjs_unacked_requeued',
  DEAD_SOCKET_RECONNECT: 'yjs_dead_socket_reconnect',
  PENDING_QUEUE_EXPIRED: 'yjs_pending_queue_expired',
  PENDING_UPDATE_PRESERVED: 'yjs_pending_update_preserved',
  PENDING_QUEUE_RESTORED: 'yjs_pending_queue_restored',
  REFRESH_BLOCKED: 'yjs_refresh_blocked',
  COMMENT_CHANGE_DETECTED: 'yjs_comment_change_detected',
  ERROR: 'yjs_error',
  EXHAUSTED_UPDATE_EXPIRED: 'yjs_exhausted_update_expired',
  TELEMETRY_DELIVERY_SUMMARY: 'yjs_telemetry_delivery_summary',
} as const;

/**
 * Pending update with metadata for forensic tracing (Tightening #8)
 */
interface PendingUpdate {
  update: Uint8Array;
  origin: any;
  updateId: string;
  timestamp: number;
  clientTs: string;
  retryCount: number;  // Preserve across disconnect
}

/** Everything this tab has changed that the server has not yet confirmed. */
export interface UnsavedStatus {
  pending: number;
  unacked: number;
  exhausted: number;
  /** Age of the oldest unconfirmed change, 0 when nothing is outstanding. */
  oldestAgeMs: number;
}

interface UnackedUpdate extends PendingUpdate {
  sentAt: number;
  exhausted?: boolean;  // Marked when max retries exceeded (Tightening #1)
  exhaustedAt?: number; // Timestamp of exhaustion (Tightening #1)
}

export interface CustomWebsocketProviderOptions {
  /** WebSocket endpoint URL */
  wsEndpoint: string;
  /** Room ID for the document */
  roomId: string;
  /** Yjs document */
  ydoc: Y.Doc;
  /** JWT token for authentication (static - will expire if tab is backgrounded) */
  token?: string;
  /**
   * Function to get fresh JWT token (preferred over static token).
   * Called on every connect/reconnect to ensure token is not expired.
   * This fixes "Signature has expired" errors when tabs are backgrounded.
   */
  getToken?: () => Promise<string>;
  /** Auto-connect on creation (default: true) */
  connect?: boolean;
  /** Reconnect delay in ms (default: 1000) */
  reconnectDelay?: number;
  /** Max reconnect delay in ms (default: 30000) */
  maxReconnectDelay?: number;
  /** Ping interval in ms (default: 30000) */
  pingInterval?: number;
  /** Telemetry context (optional) */
  telemetry?: {
    clientId?: string;
    sheetId?: string;
    userId?: string;
    userEmail?: string;
  };
}

export class CustomWebsocketProvider extends Observable<string> {
  private wsEndpoint: string;
  private roomId: string;
  private ydoc: Y.Doc;
  private token: string;
  private getToken?: () => Promise<string>;
  private ws: WebSocket | null = null;
  private reconnectDelay: number;
  private maxReconnectDelay: number;
  private currentReconnectDelay: number;
  private pingInterval: number;
  private pingTimer: NodeJS.Timeout | null = null;
  // Liveness. A socket can stay OPEN in the browser after a laptop sleep or a
  // network handoff while the TCP session behind it is gone. Nothing arrives,
  // nothing is delivered, and the first edit finds out 30 s later. We track
  // the last pong and treat two missed ones as a dead socket
  // (2026-09-03 sync audit, finding #2).
  private lastPongAt: number = 0;
  private livenessProbeTimer: NodeJS.Timeout | null = null;
  private readonly LIVENESS_PROBE_TIMEOUT_MS = 5000;
  private visibilityHandler: (() => void) | null = null;
  private onlineHandler: (() => void) | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  // Counts consecutive 403s from the token route (draft-not-yet-created race
  // vs. genuine unauthorized). Reset on any successful token fetch.
  private forbiddenRetryCount = 0;
  private syncWatchdogTimer: NodeJS.Timeout | null = null;
  private shouldConnect: boolean;
  private _synced: boolean = false;
  private _connected: boolean = false;
  private awareness: Map<string, any> = new Map();
  private localAwarenessState: any = null;
  private destroyed: boolean = false;
  private telemetry?: {
    clientId?: string;
    sheetId?: string;
    userId?: string;
    userEmail?: string;
  };

  // Queue for updates made while disconnected - prevents data loss
  private pendingUpdates: PendingUpdate[] = [];
  // Updates sent over socket but not yet acknowledged by server
  private unackedUpdates: Map<string, UnackedUpdate> = new Map();
  private ackTimers: Map<string, NodeJS.Timeout> = new Map();

  // ACK protocol configuration (Tightenings #1, #8)
  private readonly ackTimeoutMs: number = 10000;  // Fixed 10s ACK wait per attempt
  private readonly MAX_SOFT_RETRIES = 2;   // Retries 1-2: soft (same socket)
  // Hard retry is retry #3 (MAX_SOFT_RETRIES + 1)
  private readonly MAX_TOTAL_RETRIES = 3;  // After 3 retries → exhausted

  // Queue limit constants (Tightening #3, #6)
  private readonly MAX_PENDING_UPDATES_COUNT = 1000;
  private readonly MAX_PENDING_UPDATES_BYTES = 10 * 1024 * 1024; // 10MB
  private readonly MAX_UNACKED_UPDATES_COUNT = 500;
  private readonly MAX_UNACKED_UPDATES_BYTES = 5 * 1024 * 1024; // 5MB

  // Queue full flag for blocking user edits (Tightening #6)
  private queueFull = false;

  // Track which exhausted updates already have scheduled retries (Bugfix P2)
  private exhaustedRetryScheduled = new Set<string>();

  // Persist pending/unacked queues to survive page refresh
  // v2: one record per (room, browser tab). v1 keyed records by room only, so
  // two tabs on the same sheet overwrote each other's unsent edits every 250 ms
  // and a record was never expired or removed (2026-09-03 sync audit, #6).
  private queueDbName = 'yjs-offline-queue-v2';
  private legacyQueueDbName = 'yjs-offline-queue-v1';
  private queueStoreName = 'queues';
  private readonly QUEUE_RECORD_MAX_AGE_MS = 24 * 60 * 60 * 1000;
  // Stable per browser tab across refreshes (sessionStorage), but never shared
  // with another tab. Deliberately NOT the telemetry tabId: updateIds embed
  // that one with a per-load counter, so reusing it after a refresh would make
  // the server drop new updates as duplicates.
  // A duplicated tab starts with a COPY of sessionStorage, so the id alone is
  // not enough: the tab also holds a Web Lock named after it for as long as it
  // lives, and a tab that finds the lock taken mints a fresh id instead.
  private lockerTabId: string;
  private static lockerTabIdClaim: Promise<string> | null = null;
  private queuePersistTimer: NodeJS.Timeout | null = null;
  // Nothing touches the durable record until the restore has read it: a
  // persist or delete issued earlier would overwrite edits this tab has not
  // replayed yet. destroy() before that point parks its final state here and
  // the restore completion writes it.
  private queueRestored = false;
  // v1 is read at most once per page load and never re-opened after we ask for
  // the database to be dropped. Re-opening is what makes this dangerous: a
  // deleteDatabase blocked by another tab's open connection (the currently
  // deployed provider never closes its IndexedDB handles) leaves every later
  // open() of that name queued behind it, and this read gates connect().
  private static legacyQueueRead: Promise<Map<string, any>> | null = null;
  private static legacyQueueGone = false;
  // Every read and write of a given room's record goes through one chain,
  // joined in call order. A remount destroys one provider and constructs its
  // replacement straight away; without this the replacement's restore can
  // overtake its predecessor's final write, miss those edits, and then
  // overwrite them. Keyed by room rather than by record: the locker id is
  // settled asynchronously, and a provider torn down before it settles must
  // still serialise against the one that replaces it.
  private static queueIo = new Map<string, Promise<unknown>>();
  private readonly LEGACY_READ_TIMEOUT_MS = 2000;

  // Browser unload guard to prevent silent data loss
  private beforeUnloadHandler: ((event: BeforeUnloadEvent) => void) | null = null;

  // Telemetry: track sync timing
  private syncStartTime: number = 0;

  // Comment tracking: cache for detecting changes to ourComment/notes
  private commentCache = new Map<string, { ourComment: string | null; notes: string | null }>();

  // Tracking IDs for telemetry and debugging
  private tabId: string;
  private sessionId: string;
  private updateCounter: number = 0;
  private eventSeq: number = 0;
  private telemetrySendFailures: number = 0;

  // Update handler to listen for local Yjs changes
  private updateHandler: (update: Uint8Array, origin: any) => void;

  private isForbiddenError(error: any): boolean {
    return error?.response?.status === 403 || error?.status === 403 || error?.code === 403;
  }

  constructor(options: CustomWebsocketProviderOptions) {
    super();

    this.wsEndpoint = options.wsEndpoint;
    this.roomId = options.roomId;
    this.ydoc = options.ydoc;
    this.token = options.token || '';
    this.getToken = options.getToken;
    this.reconnectDelay = options.reconnectDelay ?? 1000;
    this.maxReconnectDelay = options.maxReconnectDelay ?? 30000;
    this.currentReconnectDelay = this.reconnectDelay;
    this.pingInterval = options.pingInterval ?? 30000;
    this.shouldConnect = options.connect ?? true;
    this.telemetry = options.telemetry;

    // Generate unique IDs for telemetry tracking
    this.tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.lockerTabId = CustomWebsocketProvider.readLockerTabId();

    console.log(`[YJS-WS] Initializing provider for room: ${this.roomId}, endpoint: ${this.wsEndpoint}`);
    console.log(`[YJS-WS] Initialized with tabId: ${this.tabId}, sessionId: ${this.sessionId}`);

    // Listen for local Yjs updates to broadcast
    this.updateHandler = (update: Uint8Array, origin: any) => {
      if (origin === 'remote') return;

      const updateId = this.generateUpdateId();
      const clientTs = new Date().toISOString();
      const action = (this._connected && this._synced) ? 'sent' : 'queued';

      // Keep legacy event for continuity while adding explicit lifecycle events.
      this.logTelemetry('yjs_update_processed', {
        updateId,
        clientTs,
        bytes: update.length,
        origin: String(origin),
        action,
        connected: this._connected,
        synced: this._synced,
        queueSize: this.pendingUpdates.length + (action === 'queued' ? 1 : 0)
      });

      if (this._connected && this._synced) {
        // Try to send immediately, but queue if send fails (socket closed mid-send)
        try {
          this.sendUpdate(update, {
            updateId,
            origin: String(origin),
            clientTs,
            retryCount: 0
          });
        } catch (error) {
          // Socket closed between check and send - queue it instead
          console.warn(`[YJS-WS] ⚠️ Send failed (${error}), queueing update: ${updateId}`);
          this.logTelemetry('yjs_update_send_failed', {
            updateId,
            clientTs,
            bytes: update.length,
            origin: String(origin),
            error: String(error),
            reason: 'socket_closed_during_send'
          });

          // Over capacity: warn, but still queue. This handler runs after the
          // Y.Doc transaction has committed, so returning here would not
          // prevent the edit, it would drop a clock-bearing update and open
          // the same gap this provider now guards against (PR #621 review P1).
          if (this.queueFull) this.warnQueueOverCapacity(updateId, update.length, origin);

          this.logTelemetry('yjs_update_queued', {
            updateId,
            clientTs,
            bytes: update.length,
            origin: String(origin),
            connected: this._connected,
            synced: this._synced,
            queueSize: this.pendingUpdates.length + 1,
            fallbackFromSendFailure: true
          });
          this.pendingUpdates.push({
            update,
            origin,
            updateId,
            timestamp: Date.now(),
            clientTs,
            retryCount: 0,
          });
          this.enforceQueueLimits();  // Enforce limits after queuing (Tightenings #3, #6)
          this.persistQueues();
        }
      } else {
        // Over capacity: warn, but still queue (see the note in the send-failure
        // branch above; a committed update must never be dropped).
        if (this.queueFull) this.warnQueueOverCapacity(updateId, update.length, origin);

        console.log(`[YJS-WS] 📋 Queuing local update (${update.length} bytes, origin: ${origin}, updateId: ${updateId}) - connected: ${this._connected}, synced: ${this._synced}, queue size: ${this.pendingUpdates.length + 1}`);
        this.logTelemetry('yjs_update_queued', {
          updateId,
          clientTs,
          bytes: update.length,
          origin: String(origin),
          connected: this._connected,
          synced: this._synced,
          queueSize: this.pendingUpdates.length + 1
        });
        this.pendingUpdates.push({
          update,
          origin,
          updateId,
          timestamp: Date.now(),
          clientTs,
          retryCount: 0,
        });
        this.enforceQueueLimits();  // Enforce limits after queuing (Tightenings #3, #6)
        this.persistQueues();
      }
    };
    this.ydoc.on('update', this.updateHandler);

    this.registerBeforeUnloadGuard();
    this.registerLivenessListeners();

    // Restore pending/unacked queues before first connect.
    // This allows replay of unsent edits after refresh.
    void this.loadPersistedQueues().finally(() => {
      if (this.shouldConnect) {
        this.connect();
      }
    });
  }

  get synced(): boolean {
    return this._synced;
  }

  get connected(): boolean {
    return this._connected;
  }

  /**
   * Generate unique update ID for tracking individual updates through their lifecycle
   */
  private generateUpdateId(): string {
    return `upd_${this.tabId}_${++this.updateCounter}`;
  }

  /**
   * Check if queues have capacity for new updates (Bugfix Round 2 #2)
   * Uses hard limits (100%) for recovery check - users should be unblocked as soon as possible
   */
  private hasCapacity(): boolean {
    const pendingBytes = this.pendingUpdates.reduce((sum, p) => sum + p.update.length, 0);
    const unackedBytes = Array.from(this.unackedUpdates.values())
      .reduce((sum, u) => sum + u.update.length, 0);

    return (
      this.pendingUpdates.length < this.MAX_PENDING_UPDATES_COUNT &&
      pendingBytes < this.MAX_PENDING_UPDATES_BYTES &&
      this.unackedUpdates.size < this.MAX_UNACKED_UPDATES_COUNT &&
      unackedBytes < this.MAX_UNACKED_UPDATES_BYTES
    );
  }

  /**
   * Mark update as exhausted (Tightening #1)
   * Called when max retries exceeded - keeps update in queue instead of discarding
   */
  private markAsExhausted(updateId: string, update: UnackedUpdate): void {
    console.error(`[YJS-WS] ❌ Update ${updateId} retry exhausted (kept in queue)`);

    this.clearAckTimer(updateId);
    this.unackedUpdates.set(updateId, {
      ...update,
      exhausted: true,
      exhaustedAt: Date.now()
    });

    this.logTelemetry(TELEMETRY_EVENT_TYPES.RETRY_EXHAUSTED, {
      updateId,
      origin: String(update.origin),
      retryCount: update.retryCount,
      maxRetries: this.MAX_TOTAL_RETRIES,
      action: 'kept_in_queue'
    });

    this.persistQueues();
  }

  /**
   * Enforce queue size limits (Tightenings #3, #6).
   * Nothing is ever evicted: dropping any update from this tab, whatever its
   * origin, opens a clock gap that strands every later update (see handleSync).
   * When a queue is over its limit we flag it so the UI can warn; new updates
   * are still queued, because they are already committed to the Y.Doc.
   */
  private enforceQueueLimits(): void {
    const totalPendingBytes = this.pendingUpdates.reduce((sum, p) => sum + p.update.length, 0);
    const totalUnackedBytes = Array.from(this.unackedUpdates.values())
      .reduce((sum, u) => sum + u.update.length, 0);

    const pendingOver =
      this.pendingUpdates.length > this.MAX_PENDING_UPDATES_COUNT ||
      totalPendingBytes > this.MAX_PENDING_UPDATES_BYTES;
    const unackedOver =
      this.unackedUpdates.size > this.MAX_UNACKED_UPDATES_COUNT ||
      totalUnackedBytes > this.MAX_UNACKED_UPDATES_BYTES;

    if (!pendingOver && !unackedOver) return;

    if (!this.queueFull) {
      console.error(`[YJS-WS] ❌ Update queue over limit - warning UI until the server catches up`);
    }
    this.logTelemetry(TELEMETRY_EVENT_TYPES.QUEUE_LIMIT_BLOCKED, {
      queueType: pendingOver ? 'pending' : 'unacked',
      queueCount: pendingOver ? this.pendingUpdates.length : this.unackedUpdates.size,
      queueBytes: pendingOver ? totalPendingBytes : totalUnackedBytes,
      reason: 'over_limit_no_eviction',
    });
    this.queueFull = true;
  }

  /**
   * Clean up exhausted updates older than 24h (Tightening #1)
   * Prevents unbounded growth of exhausted updates
   */
  private cleanupExhaustedUpdates(): void {
    const MAX_EXHAUSTED_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
    const now = Date.now();

    for (const [updateId, update] of this.unackedUpdates) {
      if (update.exhausted && update.exhaustedAt) {
        const age = now - update.exhaustedAt;
        if (age > MAX_EXHAUSTED_AGE_MS) {
          console.warn(`[YJS-WS] ⏰ Removing exhausted update after 24h: ${updateId}`);
          this.unackedUpdates.delete(updateId);
          this.logTelemetry(TELEMETRY_EVENT_TYPES.EXHAUSTED_UPDATE_EXPIRED, {
            updateId,
            origin: String(update.origin),
            ageMs: age
          });
        }
      }
    }
    this.persistQueues();
  }

  /**
   * Retry previously exhausted updates with jitter (Tightenings #1, #4; Bugfix P2)
   * Called after sync completes
   * Adds random 0-5s delay to prevent thundering herd
   * Prevents duplicate scheduling with exhaustedRetryScheduled guard
   */
  private retryExhaustedUpdates(): void {
    const exhausted = Array.from(this.unackedUpdates.values())
      .filter(u => u.exhausted && !this.exhaustedRetryScheduled.has(u.updateId));  // Bugfix P2: Filter already scheduled

    if (exhausted.length === 0) return;

    console.log(`[YJS-WS] 🔄 Retrying ${exhausted.length} previously exhausted updates with jitter`);

    exhausted.forEach((update) => {
      // Add jitter: 0-5000ms random delay
      const jitterMs = Math.random() * 5000;

      // Bugfix P2: Mark as scheduled to prevent duplicates
      this.exhaustedRetryScheduled.add(update.updateId);

      setTimeout(() => {
        if (this.destroyed || !this._connected) {
          this.exhaustedRetryScheduled.delete(update.updateId);  // Bugfix P2: Clean up on abort
          return;
        }

        // Reset retry state for new attempt
        this.unackedUpdates.set(update.updateId, {
          ...update,
          exhausted: false,
          exhaustedAt: undefined,
          retryCount: 0,  // Fresh start
          sentAt: Date.now()
        });

        this.sendUpdate(update.update, {
          updateId: update.updateId,
          origin: String(update.origin),
          clientTs: update.clientTs,
          retryCount: 0
        });

        this.logTelemetry(TELEMETRY_EVENT_TYPES.EXHAUSTED_RETRY_ATTEMPT, {
          updateId: update.updateId,
          origin: String(update.origin),
          jitterMs: Math.round(jitterMs)
        });

        // Bugfix P2: Remove from scheduled set after retry completes
        this.exhaustedRetryScheduled.delete(update.updateId);
      }, jitterMs);
    });
  }

  /**
   * Show UI warning when queue is full of user-edits (Tightening #6)
   * Called when queueFull flag is set
   */
  private warnQueueOverCapacity(updateId: string, bytes: number, origin: unknown): void {
    console.warn(`[YJS-WS] ⚠️ Queue over capacity - still queuing update ${updateId} (${bytes} bytes, origin: ${String(origin)})`);
    this.logTelemetry(TELEMETRY_EVENT_TYPES.USER_EDIT_BLOCKED, {
      updateId,
      bytes,
      origin: String(origin),
      queueCount: this.pendingUpdates.length,
      reason: 'queue_full_still_queued'
    });
    this.showQueueFullWarning();
  }

  private showQueueFullWarning(): void {
    console.error(`[YJS-WS] ⚠️ CRITICAL: Update queue over capacity - the server is not keeping up`);

    // Emit event that UI can listen to
    this.emit('queue-full', [{
      pendingCount: this.pendingUpdates.length,
      unackedCount: this.unackedUpdates.size,
      message: 'Saving is falling behind: many changes are waiting for the server. Keep this tab open until it catches up.'
    }]);
  }

  private emitQueueRecovered(): void {
    this.emit('queue-recovered', [{
      pendingCount: this.pendingUpdates.length,
      unackedCount: this.unackedUpdates.size,
      message: 'Queue recovered'
    }]);
  }

  /**
   * Connect to WebSocket server.
   * If getToken is provided, fetches fresh token first to avoid "Signature has expired" errors.
   */
  connect(): void {
    if (this.destroyed) return;
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) return;

    this.shouldConnect = true;
    console.log(`[YJS-WS] connect() called for room: ${this.roomId}`);

    // If getToken is provided, fetch fresh token before connecting
    if (this.getToken) {
      this.getToken()
        .then((freshToken) => {
          this.token = freshToken;
          this.forbiddenRetryCount = 0;
          this.doConnect();
        })
        .catch((error) => {
          if (this.isForbiddenError(error)) {
            // A 403 from the token route usually means the draft record does not
            // exist YET (new-sheet creation race), not that the user is
            // unauthorized — the draft POST and the first connect race each
            // other. Giving up permanently here silently strands the tab in
            // local-only mode and the user's edits never reach the server
            // (2026-07-31 SearchLab incident). Keep retrying with backoff: the
            // draft appears within seconds in the race case, and a genuinely
            // unauthorized user just produces a cheap 403 every ~30s.
            this.forbiddenRetryCount++;
            console.error(
              `[YJS-WS] ❌ Room token forbidden for room: ${this.roomId} ` +
              `(attempt ${this.forbiddenRetryCount}) — retrying; draft may not exist yet`
            );
            this.emit('status', [{ status: 'forbidden' }]);
            this.logTelemetry('yjs_error', {
              message: 'room_token_forbidden',
              attempt: this.forbiddenRetryCount,
            });
            if (this.shouldConnect && !this.destroyed) {
              this.scheduleReconnect();
            }
            return;
          }

          // Fall back to an existing token only if one is available.
          if (this.token) {
            this.doConnect();
            return;
          }

          this.emit('status', [{ status: 'disconnected' }]);
          this.logTelemetry('yjs_error', { message: 'room_token_fetch_failed' });
          if (this.shouldConnect && !this.destroyed) {
            this.scheduleReconnect();
          }
        });
    } else {
      this.doConnect();
    }
  }

  /**
   * Internal method to establish WebSocket connection (called after token is ready)
   */
  private doConnect(): void {
    if (this.destroyed) return;
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) return;

    const wsUrl = `${this.wsEndpoint}?token=${encodeURIComponent(this.token)}`;
    console.log(`[YJS-WS] Connecting to: ${this.wsEndpoint} (token: ${this.token ? 'present' : 'missing'})`);
    this.ws = new WebSocket(wsUrl);
    // A socket we replaced via forceReconnect()/hard retry can fire its close
    // event long after the new one is up (the close handshake on a dead TCP
    // session times out slowly). Those late events must not touch the state
    // of the live connection.
    const socket = this.ws;
    const isStale = () => this.ws !== null && this.ws !== socket;

    this.ws.onopen = () => {
      console.log(`[YJS-WS] ✅ Connected to room: ${this.roomId}`);
      this._connected = true;
      this.currentReconnectDelay = this.reconnectDelay;
      this.emit('status', [{ status: 'connected' }]);
      this.logTelemetry('yjs_connect');

      // Subscribe to the room
      // NOTE: Pending updates are flushed AFTER receiving initial sync (in handleSync)
      // to prevent race condition where client's stale data overwrites server data
      this.send({ action: 'subscribe', roomId: this.roomId });

      // Sync watchdog: re-subscribe every 10s until initial sync arrives, socket closes,
      // or max attempts reached (avoids infinite noise under prolonged backend outage).
      if (this.syncWatchdogTimer) clearInterval(this.syncWatchdogTimer);
      let watchdogAttempts = 0;
      const MAX_WATCHDOG_ATTEMPTS = 6; // 60s total, then rely on reconnect cycle
      this.syncWatchdogTimer = setInterval(() => {
        if (this._synced || this.destroyed || !this._connected || watchdogAttempts >= MAX_WATCHDOG_ATTEMPTS) {
          if (this.syncWatchdogTimer) { clearInterval(this.syncWatchdogTimer); this.syncWatchdogTimer = null; }
          return;
        }
        watchdogAttempts++;
        console.warn(`[YJS-WS] ⚠️ Sync watchdog (${watchdogAttempts}/${MAX_WATCHDOG_ATTEMPTS}): no initial sync — re-subscribing`);
        this.send({ action: 'subscribe', roomId: this.roomId });
      }, 10000);

      // Start ping interval
      this.startPing();

      // Send local awareness if set
      if (this.localAwarenessState) {
        this.sendAwareness(this.localAwarenessState);
      }
    };

    this.ws.onclose = (event) => {
      if (isStale()) {
        console.log(`[YJS-WS] Ignoring close event from a replaced socket (code: ${event.code})`);
        return;
      }
      console.log(`[YJS-WS] ❌ Disconnected from room: ${this.roomId}, code: ${event.code}, reason: ${event.reason || 'none'}, was synced: ${this._synced}, pending updates: ${this.pendingUpdates.length}`);
      const wasSynced = this._synced;
      this._connected = false;
      this._synced = false;
      this.stopPing();
      if (wasSynced) this.emit('sync', [false]);
      if (event.code === 4003) {
        this.shouldConnect = false;
      }

      // Requeue sent-but-unacked updates so they replay after reconnect.
      // disconnect() has always done this, but network-initiated closes came
      // through here WITHOUT it: in-flight edits stranded in unackedUpdates,
      // their ack-timeout retries fired against a dead socket, and after 3
      // failures they were marked exhausted and silently lost while the UI
      // kept showing them (2026-08-03 comment/wordcount loss under a flapping
      // hotel-wifi connection). CRDT updates are idempotent and the server
      // dedups by updateId, so replaying on the next connection is safe.
      if (this.unackedUpdates.size > 0) {
        const requeued = Array.from(this.unackedUpdates.values()).map((u) => ({
          update: u.update,
          origin: u.origin,
          updateId: u.updateId,
          timestamp: u.timestamp,
          clientTs: u.clientTs,
          retryCount: u.retryCount,
        }));
        this.pendingUpdates = [...requeued, ...this.pendingUpdates];
        this.ackTimers.forEach((timer) => clearTimeout(timer));
        this.ackTimers.clear();
        this.unackedUpdates.clear();
        console.log(`[YJS-WS] ♻️ Requeued ${requeued.length} unacked update(s) for replay after reconnect`);
        this.logTelemetry('yjs_unacked_requeued', {
          count: requeued.length,
          reason: 'socket_close',
        });
        this.persistQueues();
      }

      this.emit('status', [{ status: 'disconnected' }]);
      this.logTelemetry('yjs_disconnect', {
        code: event.code,
        reason: event.reason || 'none',
      });

      // Reconnect if we should still be connected
      if (this.shouldConnect && !this.destroyed) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      if (isStale()) return;
      console.error(`[YJS-WS] ⚠️ WebSocket error for room: ${this.roomId}`, error);
      this.emit('connection-error', [error]);
      this.logTelemetry('yjs_error', { message: 'websocket_error' });
    };

    this.ws.onmessage = (event) => {
      if (isStale()) return;
      this.handleMessage(event.data);
    };
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    console.log(`[YJS-WS] 🔌 Disconnecting from room: ${this.roomId}, pending updates: ${this.pendingUpdates.length}`);
    this.shouldConnect = false;
    this.stopPing();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.syncWatchdogTimer) {
      clearInterval(this.syncWatchdogTimer);
      this.syncWatchdogTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    const wasConnected = this._connected;
    const wasSynced = this._synced;
    this._connected = false;
    this._synced = false;
    // Tell listeners ourselves. The old socket's onclose used to do this, but
    // it is ignored once a replacement socket exists (stale-socket guard), and
    // on a dead TCP session it can arrive much later anyway. Without this the
    // hook keeps connected/synced true while the new socket is still
    // connecting (PR #621 review P2).
    if (!this.destroyed) {
      if (wasSynced) this.emit('sync', [false]);
      if (wasConnected) this.emit('status', [{ status: 'disconnected' }]);
    }

    // Requeue unacked updates on disconnect (delivery is uncertain without ACK)
    if (this.unackedUpdates.size > 0) {
      const requeued = Array.from(this.unackedUpdates.values()).map((u) => ({
        update: u.update,
        origin: u.origin,
        updateId: u.updateId,
        timestamp: u.timestamp,
        clientTs: u.clientTs,
        retryCount: u.retryCount,
      }));
      this.pendingUpdates = [...requeued, ...this.pendingUpdates];
      this.ackTimers.forEach((timer) => clearTimeout(timer));
      this.ackTimers.clear();
      this.unackedUpdates.clear();
      this.logTelemetry('yjs_unacked_requeued', {
        count: requeued.length,
        reason: 'socket_disconnect',
      });
      this.persistQueues();
    }
  }

  /**
   * Destroy the provider
   */
  destroy(): void {
    const unsaved = this.getUnsavedCount();
    console.log(`[YJS-WS] 💥 Destroying provider for room: ${this.roomId}, unsent updates kept in the offline queue: ${unsaved}`);
    this.destroyed = true;
    this.disconnect();
    // disconnect() moved unacked into pending; write that final state once.
    // Once the record has been read, nothing unsent means it is removed so it
    // cannot replay later; before that, it is only ever added to.
    if (this.queuePersistTimer) {
      clearTimeout(this.queuePersistTimer);
      this.queuePersistTimer = null;
    }
    if (typeof window !== 'undefined') {
      if (!this.queueRestored) {
        // The restore has not read the record yet, so this provider cannot
        // replace it: it does not know what is in there. It can still add what
        // it holds. The append joins the chain here, synchronously, so a
        // replacement constructed straight after cannot read ahead of it, and
        // it needs nothing from the restore, which would otherwise be waiting
        // behind this very slot.
        const toAppend = [...this.pendingUpdates];
        if (toAppend.length > 0) {
          void CustomWebsocketProvider.enqueueQueueIo(this.roomId, () => this.appendToRecord(toAppend));
        }
      } else if (this.getUnsavedCount() > 0) {
        void this.persistQueuesNow();
      } else {
        void this.deletePersistedQueue();
      }
    }
    this.ydoc.off('update', this.updateHandler);
    this.unregisterBeforeUnloadGuard();
    this.unregisterLivenessListeners();
    // Clear pending updates to prevent memory leaks
    this.pendingUpdates = [];
    this.ackTimers.forEach((timer) => clearTimeout(timer));
    this.ackTimers.clear();
    this.unackedUpdates.clear();
    super.destroy();
  }

  /**
   * Manually publish a local Y.Doc update when ydoc.on('update') doesn't fire.
   * Delegates to the existing updateHandler — same send/queue/telemetry logic.
   * Used as belt-and-suspenders in multi-tab scenarios where transact() produces
   * no events despite Y.Doc state changing.
   */
  publishLocalUpdate(update: Uint8Array, origin: any): void {
    if (this.destroyed) return;
    this.updateHandler(update, origin);
  }

  /**
   * Update the JWT token and reconnect if needed
   * Called when token is refreshed to prevent "Signature has expired" errors
   */
  updateToken(newToken: string): void {
    if (this.token === newToken) return;
    console.log(`[YJS-WS] 🔑 Token updated for room: ${this.roomId}, will reconnect: ${this.shouldConnect && !this.destroyed}`);
    this.token = newToken;

    // Reconnect with the new token if we should be connected
    if (this.shouldConnect && !this.destroyed) {
      this.disconnect();
      // Small delay to ensure clean disconnect
      setTimeout(() => {
        if (!this.destroyed) {
          this.shouldConnect = true;
          this.connect();
        }
      }, 100);
    }
  }

  /**
   * Set local awareness state
   */
  setLocalAwareness(state: any): void {
    this.localAwarenessState = state;
    if (this._connected) {
      this.sendAwareness(state);
    }
  }

  /**
   * Get awareness states
   */
  getAwareness(): Map<string, any> {
    return this.awareness;
  }

  /**
   * Send a message over WebSocket.
   * @returns true if message was sent, false if socket not open
   */
  private send(message: object): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return false;  // Socket not open, send failed
    }
    this.ws.send(JSON.stringify(message));
    return true;  // Successfully sent
  }

  private sendUpdate(update: Uint8Array, telemetry?: { updateId?: string; origin?: string; clientTs?: string; retryCount?: number }): void {
    // AWS API Gateway has a 128KB message limit, use 100KB to be safe
    const MAX_CHUNK_SIZE = 100 * 1024; // 100KB

    // Log with warning for large updates (potential issue indicator)
    if (update.length > 50000) {
      console.log(`[YJS-WS] 📤 ⚠️ Sending LARGE update (${update.length} bytes) for room: ${this.roomId} - investigate if this happens frequently`);
    } else {
      console.log(`[YJS-WS] 📤 Sending update (${update.length} bytes) for room: ${this.roomId}`);
    }

    if (update.length <= MAX_CHUNK_SIZE) {
      // Small update - send directly
      const base64 = this.uint8ArrayToBase64(update);
      const sent = this.send({
        action: 'update',
        roomId: this.roomId,
        updateId: telemetry?.updateId,
        update: base64
      });
      if (!sent) {
        throw new Error('WebSocket not open - send failed');
      }
      // Log telemetry AFTER successful send
      this.logTelemetry('yjs_update_sent', {
        updateId: telemetry?.updateId,
        clientTs: telemetry?.clientTs,
        origin: telemetry?.origin,
        bytes: update.length,
        chunked: false
      });
      this.markAsUnacked(update, telemetry);
    } else {
      // Large update - split into chunks to avoid "Message too big" error
      console.log(`[YJS-WS] 📤 Splitting large update into ${Math.ceil(update.length / MAX_CHUNK_SIZE)} chunks`);
      const chunks = this.splitIntoChunks(update, MAX_CHUNK_SIZE);
      const chunkId = this.generateChunkId();

      for (let i = 0; i < chunks.length; i++) {
        const sent = this.send({
          action: 'update-chunk',
          roomId: this.roomId,
          updateId: telemetry?.updateId,
          chunkId,
          chunkIndex: i,
          totalChunks: chunks.length,
          chunk: this.uint8ArrayToBase64(chunks[i])
        });
        if (!sent) {
          throw new Error(`WebSocket not open - chunk ${i + 1}/${chunks.length} send failed`);
        }
      }

      // Log telemetry AFTER all chunks successfully sent
      this.logTelemetry('yjs_update_sent', {
        updateId: telemetry?.updateId,
        clientTs: telemetry?.clientTs,
        origin: telemetry?.origin,
        bytes: update.length,
        chunked: true,
        chunkCount: chunks.length
      });
      this.markAsUnacked(update, telemetry);
    }
  }

  private splitIntoChunks(data: Uint8Array, chunkSize: number): Uint8Array[] {
    const chunks: Uint8Array[] = [];
    for (let i = 0; i < data.length; i += chunkSize) {
      chunks.push(data.slice(i, Math.min(i + chunkSize, data.length)));
    }
    return chunks;
  }

  private generateChunkId(): string {
    // Generate a unique ID for this chunk batch
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  private sendAwareness(state: any): void {
    this.send({
      action: 'awareness',
      roomId: this.roomId,
      awareness: state
    });
  }

  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data);

      switch (message.action) {
        case 'sync':
          this.handleSync(message);
          break;
        case 'awareness':
          this.handleAwareness(message);
          break;
        case 'pong':
          this.lastPongAt = Date.now();
          this.clearLivenessProbe();
          break;
        case 'update_ack':
          this.handleUpdateAck(message);
          break;
        case 'error':
          console.error(`[YJS-WS] Server error for room ${this.roomId}: ${message.error || 'unknown'}`);
          break;
        default:
          // Ignore unknown messages
          break;
      }
    } catch (error) {
      // Ignore parse errors
    }
  }

  private handleSync(message: { type: string; roomId: string; state?: string; update?: string; source?: string }): void {
    if (message.type === 'initial') {
      console.log(`[YJS-WS] 🔄 Received initial sync for room: ${this.roomId}`);
      this.syncStartTime = Date.now();
      this.logTelemetry('yjs_sync_start', { pendingUpdatesCount: this.pendingUpdates.length });

      // Every queued update is replayed once the server state has been applied.
      // An earlier version discarded anything not tagged 'user-edit' here
      // (paste, add/delete row, reorder, defaults, ...). That is fatal rather
      // than lossy: Yjs numbers each tab's changes with a per-client clock, so
      // once one update from this tab is dropped the server parks every later
      // update from the same clientID as "pending" while still ACKing it. The
      // tab then looks healthy but nothing it writes reaches anyone until a
      // reload (2026-09-03 sync audit, finding #1; reproduced on staging
      // 2026-09-07). Replaying everything is safe: CRDT updates are idempotent
      // and the server dedups by updateId. The "stale init overwrites server"
      // case the discard was written for is covered by the post-sync
      // initializeFromRows guard in useYjsBulkBriefs.
      if (this.pendingUpdates.length > 0) {
        const originBreakdown = this.pendingUpdates.reduce((acc, p) => {
          const origin = String(p.origin);
          acc[origin] = (acc[origin] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);
        console.log(
          `[YJS-WS] ✅ PRESERVING all ${this.pendingUpdates.length} queued updates for replay after sync`,
          originBreakdown
        );
        this.logTelemetry('yjs_pending_update_preserved', {
          count: this.pendingUpdates.length,
          totalBytes: this.pendingUpdates.reduce((sum, p) => sum + p.update.length, 0),
          reason: 'replay_all',
          originBreakdown,
        });
      }

      // Apply server Y.Doc state as the source of truth
      let stateBytes = 0;
      if (message.state) {
        const serverState = this.base64ToUint8Array(message.state);
        stateBytes = serverState.length;
        console.log(`[YJS-WS] 📥 Applying server state (${serverState.length} bytes) as source of truth for room: ${this.roomId}`);
        Y.applyUpdate(this.ydoc, serverState, 'remote');
        this.checkCommentChanges('initial_sync');
      } else {
        console.log(`[YJS-WS] 📥 No server state received (new/empty document) for room: ${this.roomId}`);
      }

      // CRITICAL: Set synced state BEFORE anything that can throw (remote-update emit, replay).
      // Previously, emit('sync', [true]) was after replay — React `synced` stayed false if replay threw.
      this._synced = true;
      this.emit('sync', [true]);
      console.log(`[YJS-WS] ✅ Initial sync complete - now synced`);
      this.logTelemetry('yjs_sync_complete', {
        syncDurationMs: Date.now() - this.syncStartTime,
        stateBytes,
        wasEmpty: !message.state,
      });

      // A fresh, synced connection invalidates delivery failures from the old
      // socket: retry counts accumulated against a dead connection must not
      // carry over, or updates arrive pre-exhausted and get suppressed for
      // 24h despite a healthy link (part of the 2026-08-03 silent-loss bug).
      if (this.pendingUpdates.length > 0) {
        this.pendingUpdates = this.pendingUpdates.map((p) => ({ ...p, retryCount: 0 }));
      }
      this.exhaustedRetryScheduled.clear();

      // Clear sync watchdog timer — initial sync arrived
      if (this.syncWatchdogTimer) {
        clearInterval(this.syncWatchdogTimer);
        this.syncWatchdogTimer = null;
      }

      // Non-critical block 1: Emit remote-update for UI hydration (isolated try-catch)
      if (message.state) {
        try {
          this.emit('remote-update', [{ source: 'initial-sync', size: stateBytes }]);
        } catch (e) {
          console.error('[YJS-WS] ❌ remote-update emit failed after initial sync:', e);
        }
      }

      // Non-critical block 2: Replay queued updates (isolated try-catch)
      // Failures here must NOT flip _synced back to false.
      try {
      if (this.pendingUpdates.length > 0) {
        console.log(`[YJS-WS] 🔄 Replaying ${this.pendingUpdates.length} queued updates`);

        const toReplay = [...this.pendingUpdates];
        const successfullyReplayed: string[] = [];
        const failedReplays: typeof toReplay = [];

        for (const pending of toReplay) {
          if (!this._connected || !this._synced) {
            console.warn(`[YJS-WS] ⚠️ Connection lost during replay, stopping`);
            failedReplays.push(pending);

            this.logTelemetry('yjs_update_replay_blocked', {
              updateId: pending.updateId,
              reason: 'connection_lost_during_replay',
              connected: this._connected,
              synced: this._synced
            });
            continue;
          }

          try {
            console.log(`[YJS-WS] 🔄 Replaying: updateId=${pending.updateId}, bytes=${pending.update.length}`);
            // Updates restored from the offline queue after a refresh exist
            // only as bytes: the Y.Doc that produced them is gone. Apply them
            // here too, or the server gets the rows while this tab shows them
            // only after a second reload (the server never echoes an update
            // back to its sender). For same-session requeues this is a no-op.
            try {
              Y.applyUpdate(this.ydoc, pending.update, 'remote');
            } catch (applyError) {
              console.warn(`[YJS-WS] ⚠️ Could not apply queued update locally: ${pending.updateId}`, applyError);
            }
            this.sendUpdate(pending.update, {
              updateId: pending.updateId,
              origin: String(pending.origin),
              clientTs: pending.clientTs,
              retryCount: pending.retryCount
            });

            successfullyReplayed.push(pending.updateId);

            this.logTelemetry('yjs_update_replayed', {
              updateId: pending.updateId,
              clientTs: pending.clientTs,
              origin: String(pending.origin),
              bytes: pending.update.length,
              ageMs: Date.now() - pending.timestamp,
              reason: 'post_sync_replay'
            });
          } catch (error) {
            console.error(`[YJS-WS] ❌ Replay failed for updateId=${pending.updateId}:`, error);
            failedReplays.push(pending);

            this.logTelemetry('yjs_update_replay_failed', {
              updateId: pending.updateId,
              reason: 'send_error',
              error: String(error)
            });
          }
        }

        const sentIds = new Set(successfullyReplayed);
        this.pendingUpdates = this.pendingUpdates.filter(
          p => !sentIds.has(p.updateId)
        );
        this.persistQueues();

        if (this.queueFull && this.hasCapacity()) {
          this.queueFull = false;
          console.log(`[YJS-WS] ✅ Queue recovered after sync replay - resuming user edits`);
          this.emitQueueRecovered();
        }

        if (failedReplays.length === 0) {
          console.log(`[YJS-WS] ✅ Successfully replayed all ${successfullyReplayed.length} queued updates`);
          this.logTelemetry('yjs_replay_complete', {
            count: successfullyReplayed.length,
            totalBytes: toReplay.reduce((sum, p) => sum + p.update.length, 0),
            failedCount: 0
          });
        } else {
          console.warn(`[YJS-WS] ⚠️ Replay incomplete: ${successfullyReplayed.length} succeeded, ${failedReplays.length} failed (requeued)`);
          this.logTelemetry('yjs_replay_partial', {
            successCount: successfullyReplayed.length,
            failedCount: failedReplays.length,
            requeuedUpdateIds: failedReplays.map(p => p.updateId)
          });
        }
      }

      this.retryExhaustedUpdates();
      this.cleanupExhaustedUpdates();
      } catch (e) {
        console.error('[YJS-WS] ❌ Replay/cleanup failed after initial sync (synced state preserved):', e);
      }
    } else if (message.type === 'update') {
      // Incremental update from another client
      if (message.update) {
        const update = this.base64ToUint8Array(message.update);
        console.log(`[YJS-WS] 📥 Received remote update (${update.length} bytes) from: ${message.source || 'peer'} for room: ${this.roomId}`);
        Y.applyUpdate(this.ydoc, update, 'remote');

        // Fix 2: Implicit sync — promote to synced only after Y.applyUpdate succeeds.
        // If we successfully applied a peer update, we ARE synced. Missing the formal
        // initial handshake shouldn't freeze the tab.
        if (!this._synced) {
          console.warn('[YJS-WS] ⚠️ Received incremental update before initial sync — forcing synced state');
          this._synced = true;
          this.emit('sync', [true]);
          // Reuse existing accepted telemetry event — no new event type needed
          this.logTelemetry('yjs_sync_complete', {
            syncDurationMs: this.syncStartTime > 0 ? Date.now() - this.syncStartTime : -1,
            stateBytes: update.length,
            wasEmpty: false,
            trigger: 'implicit_sync_on_incremental_update',
          });
          // Clear sync watchdog timer
          if (this.syncWatchdogTimer) {
            clearInterval(this.syncWatchdogTimer);
            this.syncWatchdogTimer = null;
          }
        }
        this.logTelemetry('yjs_update_applied_remote', {
          source: message.source || 'peer',
          bytes: update.length
        });
        this.checkCommentChanges('remote_update_' + (message.source || 'peer'));
        this.emit('remote-update', [{ source: message.source || 'peer', size: update.length }]);
      }
    }
  }

  private handleAwareness(message: { roomId: string; data: any; from?: string }): void {
    if (message.from) {
      this.awareness.set(message.from, message.data);
      this.emit('awareness', [this.awareness]);
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;

    console.log(`[YJS-WS] Scheduling reconnect in ${this.currentReconnectDelay}ms for room: ${this.roomId}`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, this.currentReconnectDelay);

    // Exponential backoff
    this.currentReconnectDelay = Math.min(
      this.currentReconnectDelay * 2,
      this.maxReconnectDelay
    );
  }

  private startPing(): void {
    this.stopPing();
    this.lastPongAt = Date.now();
    this.pingTimer = setInterval(() => {
      const silentForMs = Date.now() - this.lastPongAt;
      if (silentForMs >= this.pingInterval * 2) {
        this.forceReconnect('pong_timeout', { silentForMs });
        return;
      }
      this.send({ action: 'ping' });
    }, this.pingInterval);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    this.clearLivenessProbe();
  }

  /**
   * Send one ping and reconnect if no pong arrives within
   * LIVENESS_PROBE_TIMEOUT_MS. Called when the tab becomes visible again or the
   * browser reports it is back online: both are moments where a half-open
   * socket is likely and the user is about to start editing.
   */
  private probeLiveness(reason: string): void {
    if (this.destroyed || !this._connected || this.livenessProbeTimer) return;
    if (!this.send({ action: 'ping' })) return;
    this.livenessProbeTimer = setTimeout(() => {
      this.livenessProbeTimer = null;
      this.forceReconnect(`probe_timeout:${reason}`, {
        silentForMs: Date.now() - this.lastPongAt,
      });
    }, this.LIVENESS_PROBE_TIMEOUT_MS);
  }

  private clearLivenessProbe(): void {
    if (this.livenessProbeTimer) {
      clearTimeout(this.livenessProbeTimer);
      this.livenessProbeTimer = null;
    }
  }

  /**
   * Drop a socket we believe is dead and open a new one right away. Unacked
   * updates move to the pending queue inside disconnect() and are replayed
   * after the next initial sync, so nothing is lost by doing this eagerly.
   */
  private forceReconnect(reason: string, meta: Record<string, unknown> = {}): void {
    if (this.destroyed || !this._connected) return;
    console.warn(`[YJS-WS] 💀 Socket looks dead (${reason}) - reconnecting room: ${this.roomId}`);
    this.logTelemetry(TELEMETRY_EVENT_TYPES.DEAD_SOCKET_RECONNECT, {
      reason,
      ...meta,
      unackedCount: this.unackedUpdates.size,
      pendingCount: this.pendingUpdates.length,
    });
    this.disconnect();
    this.shouldConnect = true;
    this.connect();
  }

  private registerLivenessListeners(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined' || this.visibilityHandler) return;
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') this.probeLiveness('visibilitychange');
    };
    this.onlineHandler = () => this.probeLiveness('online');
    document.addEventListener('visibilitychange', this.visibilityHandler);
    window.addEventListener('online', this.onlineHandler);
  }

  private unregisterLivenessListeners(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
  }

  private uint8ArrayToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToUint8Array(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Check for comment/notes field changes after Y.applyUpdate.
   * Compares current Y.Doc state against cached values and logs diffs.
   */
  private checkCommentChanges(trigger: string): void {
    try {
      const yRows = this.ydoc.getMap<Y.Map<any>>('rows');
      const changes: Array<{ rowId: string; field: string; old: string | null; new: string | null }> = [];

      yRows.forEach((yRow: Y.Map<any>, rowId: string) => {
        const ourComment = yRow.get('ourComment') ?? null;
        const notes = yRow.get('notes') ?? null;
        const cached = this.commentCache.get(rowId);

        if (cached) {
          if (cached.ourComment !== ourComment) {
            changes.push({ rowId, field: 'ourComment', old: cached.ourComment, new: ourComment });
          }
          if (cached.notes !== notes) {
            changes.push({ rowId, field: 'notes', old: cached.notes, new: notes });
          }
        }
        this.commentCache.set(rowId, { ourComment, notes });
      });

      if (changes.length > 0) {
        const cleared = changes.filter(c => c.old && !c.new);
        console.warn(`[YJS-WS] ⚠️ Comment changes after ${trigger}: ${changes.length} total, ${cleared.length} CLEARED`, changes);
        this.logTelemetry('yjs_comment_change_detected', {
          trigger,
          totalChanges: changes.length,
          cleared: cleared.length
        });
        changes.slice(0, 20).forEach((change) => {
          this.logTelemetry('yjs_comment_field_changed', {
            trigger,
            rowId: change.rowId,
            field: change.field,
            beforeHash: this.hashString(change.old),
            afterHash: this.hashString(change.new),
            beforeLen: change.old ? change.old.length : 0,
            afterLen: change.new ? change.new.length : 0
          });
        });
      }
    } catch (e) {
      // Don't let tracking break sync flow
    }
  }

  private hashString(value: string | null): string {
    const input = value || '';
    let hash = 2166136261;
    for (let i = 0; i < input.length; i++) {
      hash ^= input.charCodeAt(i);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    return (hash >>> 0).toString(16);
  }

  private handleUpdateAck(message: { roomId?: string; updateId?: string; serverTs?: string }): void {
    const updateId = message.updateId;
    if (!updateId) return;

    const unacked = this.unackedUpdates.get(updateId);
    if (!unacked) return;

    console.log(
      `[YJS-WS] ✅ ACK received for updateId=${updateId} ` +
      `(latency=${Date.now() - unacked.sentAt}ms, retry=${unacked.retryCount})`
    );

    this.clearAckTimer(updateId);
    this.unackedUpdates.delete(updateId);
    this.exhaustedRetryScheduled.delete(updateId);  // Bugfix P2: Clean up scheduled tracking
    this.logTelemetry('yjs_update_acked', {
      updateId,
      origin: String(unacked.origin),
      bytes: unacked.update.length,
      ackLatencyMs: Date.now() - unacked.sentAt,
      retryCount: unacked.retryCount,
      serverTs: message.serverTs,
    });
    this.persistQueues();

    // Bugfix P1: Reset queueFull if queues have recovered capacity
    if (this.queueFull && this.hasCapacity()) {
      this.queueFull = false;
      console.log(`[YJS-WS] ✅ Queue recovered - resuming user edits`);
      this.emitQueueRecovered();
    }
  }

  private markAsUnacked(update: Uint8Array, telemetry?: { updateId?: string; origin?: string; clientTs?: string; retryCount?: number }): void {
    const updateId = telemetry?.updateId;
    if (!updateId) return;
    const retryCount = telemetry?.retryCount ?? 0;

    this.clearAckTimer(updateId);

    // Keep the moment the user made the change across resends and replays so
    // the unsaved-age reported to the UI does not reset on every retry.
    const createdAt = telemetry?.clientTs ? Date.parse(telemetry.clientTs) : NaN;
    const timestamp = Number.isFinite(createdAt)
      ? createdAt
      : (this.unackedUpdates.get(updateId)?.timestamp ?? Date.now());

    this.unackedUpdates.set(updateId, {
      update,
      origin: telemetry?.origin ?? 'unknown',
      updateId,
      timestamp,
      clientTs: telemetry?.clientTs || new Date().toISOString(),
      sentAt: Date.now(),
      retryCount,
    });
    this.persistQueues();

    // ACK timeout handler with soft/hard retry strategy (Tightenings #1, #2, #8)
    const timer = setTimeout(() => {
      const stillUnacked = this.unackedUpdates.get(updateId);
      if (!stillUnacked || this.destroyed) return;

      const nextRetryCount = stillUnacked.retryCount + 1;

      // Check if exhausted (Tightening #1)
      if (nextRetryCount > this.MAX_TOTAL_RETRIES) {
        this.markAsExhausted(updateId, stillUnacked);
        return;
      }

      // Determine retry strategy (Tightening #2)
      const isSoftRetry = nextRetryCount <= this.MAX_SOFT_RETRIES;
      const backoffMs = this.ackTimeoutMs * Math.pow(2, stillUnacked.retryCount);

      this.unackedUpdates.set(updateId, {
        ...stillUnacked,
        retryCount: nextRetryCount,
      });

      this.logTelemetry(TELEMETRY_EVENT_TYPES.ACK_TIMEOUT, {
        updateId,
        origin: String(stillUnacked.origin),
        timeoutMs: this.ackTimeoutMs,
        retryCount: nextRetryCount,
        retryType: isSoftRetry ? 'soft' : 'hard',
        nextRetryInMs: isSoftRetry ? 0 : backoffMs
      });

      if (isSoftRetry) {
        // Soft retry: resend on SAME connection (Tightening #2)
        console.log(`[YJS-WS] 🔄 Soft retry ${nextRetryCount}/${this.MAX_SOFT_RETRIES} - resending without reconnect`);

        if (this._connected && this._synced) {
          this.sendUpdate(stillUnacked.update, {
            updateId,
            origin: String(stillUnacked.origin),
            clientTs: stillUnacked.clientTs,
            retryCount: nextRetryCount
          });
        } else {
          // Connection lost during soft retry, move to pending
          this.clearAckTimer(updateId);
          this.unackedUpdates.delete(updateId);
          this.pendingUpdates.unshift({
            update: stillUnacked.update,
            origin: stillUnacked.origin,
            updateId,
            timestamp: stillUnacked.timestamp,
            clientTs: stillUnacked.clientTs,
            retryCount: nextRetryCount
          });
          this.persistQueues();
        }
      } else {
        // Hard retry: force reconnect (Tightening #2)
        console.log(`[YJS-WS] 🔌 Hard retry ${nextRetryCount}/${this.MAX_TOTAL_RETRIES} - forcing reconnect after ${backoffMs}ms`);

        if (this._connected) {
          // Socket is up - disconnect and schedule reconnect
          this.disconnect();
          this.shouldConnect = true;

          setTimeout(() => {
            if (!this.destroyed && this.shouldConnect) {
              this.connect();
            }
          }, backoffMs);
        } else {
          // Socket already down - move to pending queue for next connection (Bugfix Round 2 #1)
          console.warn(`[YJS-WS] ⚠️ Hard retry timeout fired but already disconnected - moving to pending queue`);
          this.clearAckTimer(updateId);
          this.unackedUpdates.delete(updateId);
          this.pendingUpdates.unshift({
            update: stillUnacked.update,
            origin: stillUnacked.origin,
            updateId,
            timestamp: stillUnacked.timestamp,
            clientTs: stillUnacked.clientTs,
            retryCount: nextRetryCount
          });
          this.persistQueues();

          // Schedule reconnect if shouldConnect is true
          if (this.shouldConnect && !this.destroyed) {
            setTimeout(() => {
              if (!this.destroyed && this.shouldConnect) {
                this.connect();
              }
            }, backoffMs);
          }
        }
      }
    }, this.ackTimeoutMs);
    this.ackTimers.set(updateId, timer);
  }

  private clearAckTimer(updateId: string): void {
    const timer = this.ackTimers.get(updateId);
    if (!timer) return;
    clearTimeout(timer);
    this.ackTimers.delete(updateId);
  }

  private getUnsavedCount(): number {
    return this.pendingUpdates.length + this.unackedUpdates.size;
  }

  /**
   * Snapshot of what this tab has changed that the server has not confirmed.
   * Emitted as 'unsaved' whenever the queues change so the UI can show that
   * saving has stalled instead of staying green while updates pile up
   * (2026-09-03 sync audit, finding #3).
   */
  getUnsavedStatus(): UnsavedStatus {
    const now = Date.now();
    let oldest = now;
    let exhausted = 0;
    for (const p of this.pendingUpdates) oldest = Math.min(oldest, p.timestamp);
    for (const u of this.unackedUpdates.values()) {
      oldest = Math.min(oldest, u.timestamp);
      if (u.exhausted) exhausted++;
    }
    const outstanding = this.pendingUpdates.length + this.unackedUpdates.size;
    return {
      pending: this.pendingUpdates.length,
      unacked: this.unackedUpdates.size,
      exhausted,
      oldestAgeMs: outstanding > 0 ? now - oldest : 0,
    };
  }

  private emitUnsaved(): void {
    this.emit('unsaved', [this.getUnsavedStatus()]);
  }

  private registerBeforeUnloadGuard(): void {
    if (typeof window === 'undefined' || this.beforeUnloadHandler) return;
    this.beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      const unsaved = this.getUnsavedCount();
      if (unsaved <= 0) return;
      event.preventDefault();
      event.returnValue = `You have ${unsaved} unsaved collaboration update(s).`;
      this.logTelemetry('yjs_refresh_blocked', {
        pendingCount: this.pendingUpdates.length,
        unackedCount: this.unackedUpdates.size,
      });
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private unregisterBeforeUnloadGuard(): void {
    if (typeof window === 'undefined' || !this.beforeUnloadHandler) return;
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    this.beforeUnloadHandler = null;
  }

  private persistQueues(): void {
    this.emitUnsaved();
    if (typeof window === 'undefined') return;
    if (this.queuePersistTimer) clearTimeout(this.queuePersistTimer);
    this.queuePersistTimer = setTimeout(() => {
      void this.persistQueuesNow();
    }, 250);
  }

  private get queueRecordKey(): string {
    return `${this.roomId}|${this.lockerTabId}`;
  }

  private static readonly LOCKER_TAB_ID_KEY = 'yjs-locker-tab-id';

  private static mintLockerTabId(): string {
    return `ltab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private static storeLockerTabId(id: string): void {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(CustomWebsocketProvider.LOCKER_TAB_ID_KEY, id);
    } catch {
      // sessionStorage unavailable (privacy mode); the id lives for this load only
    }
  }

  private static readLockerTabId(): string {
    if (typeof window === 'undefined') return CustomWebsocketProvider.mintLockerTabId();
    try {
      const existing = window.sessionStorage.getItem(CustomWebsocketProvider.LOCKER_TAB_ID_KEY);
      if (existing) return existing;
    } catch {
      // fall through to a fresh id
    }
    const fresh = CustomWebsocketProvider.mintLockerTabId();
    CustomWebsocketProvider.storeLockerTabId(fresh);
    return fresh;
  }

  /**
   * Resolve the locker id this tab may use. Claimed once per page and shared by
   * every provider on it (several rooms, StrictMode remounts), because the Web
   * Lock is per origin, not per tab: a second request from the same page would
   * otherwise see its own lock as "taken".
   */
  private static claimLockerTabId(): Promise<string> {
    if (!CustomWebsocketProvider.lockerTabIdClaim) {
      CustomWebsocketProvider.lockerTabIdClaim = CustomWebsocketProvider.claimLockerTabIdOnce();
    }
    return CustomWebsocketProvider.lockerTabIdClaim;
  }

  private static async claimLockerTabIdOnce(): Promise<string> {
    const candidate = CustomWebsocketProvider.readLockerTabId();
    const locks: any = typeof navigator !== 'undefined' ? (navigator as any).locks : undefined;
    // No Web Locks (very old browser, insecure context): keep the sessionStorage
    // id, which is exactly the pre-lock behaviour.
    if (!locks || typeof locks.request !== 'function') return candidate;

    const claim = (id: string) => new Promise<'granted' | 'held' | 'error'>((resolve) => {
      try {
        locks.request(`yjs-locker-tab:${id}`, { ifAvailable: true }, (lock: unknown) => {
          if (!lock) { resolve('held'); return undefined; }
          resolve('granted');
          // Never settle: the lock is released when this tab unloads.
          return new Promise<void>(() => {});
        }).catch(() => resolve('error'));
      } catch {
        resolve('error');
      }
    });

    const outcome = await claim(candidate);
    // 'held' means another live tab owns this id, i.e. we are a duplicate
    // ("Duplicate tab", window.open with copied sessionStorage). On 'error'
    // the lock API is unusable; keeping the candidate beats minting a new id
    // on every refresh.
    if (outcome !== 'held') return candidate;
    const fresh = CustomWebsocketProvider.mintLockerTabId();
    CustomWebsocketProvider.storeLockerTabId(fresh);
    await claim(fresh);
    return fresh;
  }

  private buildQueueRecord(pending: PendingUpdate[], unacked: UnackedUpdate[]): Record<string, unknown> {
    return {
      key: this.queueRecordKey,
      roomId: this.roomId,
      tabId: this.lockerTabId,
      updatedAt: Date.now(),
      pending: pending.map((p) => ({
        ...p,
        update: this.uint8ArrayToBase64(p.update),
      })),
      unacked: unacked.map((u) => ({
        ...u,
        update: this.uint8ArrayToBase64(u.update),
      })),
    };
  }

  /**
   * Join the chain for one record. Callers must reach this synchronously, so
   * that call order is the order the operations run in.
   */
  private static enqueueQueueIo<T>(key: string, work: () => Promise<T>): Promise<T> {
    const previous = CustomWebsocketProvider.queueIo.get(key) ?? Promise.resolve();
    const run = previous.then(work, work);
    const tail = run.then(() => undefined, () => undefined);
    CustomWebsocketProvider.queueIo.set(key, tail);
    void tail.then(() => {
      if (CustomWebsocketProvider.queueIo.get(key) === tail) {
        CustomWebsocketProvider.queueIo.delete(key);
      }
    });
    return run;
  }

  /** Raw write. Callers must already hold a place in the chain. */
  private async writeRecord(payload: Record<string, unknown>): Promise<boolean> {
    try {
      const db = await this.openQueueDb();
      const tx = db.transaction(this.queueStoreName, 'readwrite');
      tx.objectStore(this.queueStoreName).put(payload);
      await this.awaitTx(tx);
      db.close();
      return true;
    } catch (error) {
      // Quota, a closing connection, a private-mode restriction. The caller
      // decides what to do; nothing that is still the only copy may be deleted
      // on the strength of a write that did not land.
      console.warn(`[YJS-WS] ⚠️ Could not persist the offline queue for room: ${this.roomId}`, error);
      return false;
    }
  }

  /** Raw delete. Callers must already hold a place in the chain. */
  private async deleteRecord(): Promise<boolean> {
    try {
      const db = await this.openQueueDb();
      const tx = db.transaction(this.queueStoreName, 'readwrite');
      tx.objectStore(this.queueStoreName).delete(this.queueRecordKey);
      await this.awaitTx(tx);
      db.close();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Add unsent updates to whatever the record already holds, in one
   * transaction, without replacing anything. Used by a provider torn down
   * before its restore read the record: it has edits to save but no right to
   * overwrite entries it never saw.
   */
  private async appendToRecord(unsaved: PendingUpdate[]): Promise<boolean> {
    // The record key depends on the locker id. Settle it first, or a duplicate
    // tab torn down inside the claim window would append to the original tab's
    // record. The claim never touches this chain, so waiting here is safe.
    try {
      this.lockerTabId = await CustomWebsocketProvider.claimLockerTabId();
    } catch {
      // keep the id we have
    }
    let db: IDBDatabase | null = null;
    try {
      db = await this.openQueueDb();
      const tx = db.transaction(this.queueStoreName, 'readwrite');
      const store = tx.objectStore(this.queueStoreName);
      const existing = await new Promise<any>((resolve, reject) => {
        const req = store.get(this.queueRecordKey);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });

      const seen = new Set<string>();
      const merged: any[] = [];
      const add = (entry: any) => {
        if (!entry || typeof entry.updateId !== 'string' || seen.has(entry.updateId)) return;
        seen.add(entry.updateId);
        merged.push(entry);
      };
      // Only carry forward a record the expiry sweep would still have kept.
      // This write stamps the record as fresh, so merging an expired one would
      // hand its entries another day of life on every teardown and let a stale
      // edit replay over a colleague's newer value.
      if (this.isQueueRecordFresh(existing, Date.now())) {
        // What is already stored is older, so it stays in front.
        (existing?.pending ?? []).forEach(add);
        (existing?.unacked ?? []).forEach(add);
      } else if (existing) {
        const dropped = (existing.pending?.length ?? 0) + (existing.unacked?.length ?? 0);
        console.warn(`[YJS-WS] 🗑️ Dropping ${dropped} expired offline-queue update(s) for room: ${this.roomId}`);
        this.logTelemetry(TELEMETRY_EVENT_TYPES.PENDING_QUEUE_EXPIRED, {
          count: dropped,
          maxAgeMs: this.QUEUE_RECORD_MAX_AGE_MS,
          source: 'teardown_append',
        });
      }
      unsaved.forEach((u) => add({ ...u, update: this.uint8ArrayToBase64(u.update) }));

      store.put({
        key: this.queueRecordKey,
        roomId: this.roomId,
        tabId: this.lockerTabId,
        updatedAt: Date.now(),
        pending: merged,
        unacked: [],
      });
      await this.awaitTx(tx);
      return true;
    } catch (error) {
      console.warn(`[YJS-WS] ⚠️ Could not append to the offline queue for room: ${this.roomId}`, error);
      return false;
    } finally {
      if (db) db.close();
    }
  }

  /** Resolves true only when the record is actually on disk. */
  private persistQueuesNow(
    pending: PendingUpdate[] = this.pendingUpdates,
    unacked: UnackedUpdate[] = Array.from(this.unackedUpdates.values()),
  ): Promise<boolean> {
    // Until the restore has read the durable record, writing would replace
    // unreplayed edits with a partial in-memory view. The restore completion
    // persists whatever accumulated meanwhile.
    if (!this.queueRestored) return Promise.resolve(false);
    // Snapshot synchronously: destroy() clears the in-memory queues right after
    // asking for a final flush, and the payload must reflect what was unsent.
    const payload = this.buildQueueRecord(pending, unacked);
    return CustomWebsocketProvider.enqueueQueueIo(this.roomId, () => this.writeRecord(payload));
  }

  private deletePersistedQueue(): Promise<boolean> {
    if (!this.queueRestored) return Promise.resolve(false);
    return CustomWebsocketProvider.enqueueQueueIo(this.roomId, () => this.deleteRecord());
  }

  private toPendingList(record: any): PendingUpdate[] {
    if (!record) return [];
    const restoredPending: PendingUpdate[] = (record.pending || []).map((p: any) => ({
      ...p,
      update: this.base64ToUint8Array(p.update),
      retryCount: Number.isFinite(p.retryCount) ? p.retryCount : 0,
    }));
    // Restored unacked become pending: delivery was never confirmed, and the
    // replay after reconnect is idempotent on the server.
    const restoredUnacked: PendingUpdate[] = (record.unacked || []).map((u: any) => ({
      update: this.base64ToUint8Array(u.update),
      origin: u.origin,
      updateId: u.updateId,
      timestamp: u.timestamp,
      clientTs: u.clientTs,
      retryCount: Number.isFinite(u.retryCount) ? u.retryCount : 0,
    }));
    return [...restoredUnacked, ...restoredPending];
  }

  private isQueueRecordFresh(record: any, now: number): boolean {
    return !!record && now - (Number(record.updatedAt) || 0) <= this.QUEUE_RECORD_MAX_AGE_MS;
  }

  /**
   * Read this tab's durable queue (and this room's pre-v2 record, if any) into
   * memory, then reconcile with whatever happened while the read was in flight:
   * edits queued meanwhile are kept behind the restored ones, and a destroy()
   * that ran meanwhile gets its final persist/delete performed here.
   */
  private async loadPersistedQueues(): Promise<void> {
    if (typeof window === 'undefined' || !window.indexedDB) {
      this.queueRestored = true;
      return;
    }
    let restored: PendingUpdate[] = [];
    let legacy: any = null;
    let legacyCount = 0;
    let readFailed = false;
    try {
      // Settle the tab identity before any key-based read or write.
      this.lockerTabId = await CustomWebsocketProvider.claimLockerTabId();
      // Torn down already: destroy() has taken over, with its own place in the
      // chain. Reading here would only queue behind that write and hold up the
      // replacement's restore.
      if (this.destroyed) return;

      // v1 kept one record per room, shared by every tab. Carry it over rather
      // than drop it: on the first load after this deploy it can hold edits the
      // previous session never got to send.
      legacy = await this.readLegacyQueueRecord();

      const now = Date.now();
      // Joined to this record's chain synchronously, so a predecessor's final
      // write on a remount is already ahead of this read.
      const { record, expiredKeys } = await CustomWebsocketProvider.enqueueQueueIo(
        this.roomId,
        () => this.readOwnQueueRecord(now),
      );

      const legacyFresh = this.isQueueRecordFresh(legacy, now);
      const expiredCount = expiredKeys.length + (legacy && !legacyFresh ? 1 : 0);
      if (expiredCount > 0) {
        this.logTelemetry(TELEMETRY_EVENT_TYPES.PENDING_QUEUE_EXPIRED, {
          count: expiredCount,
          maxAgeMs: this.QUEUE_RECORD_MAX_AGE_MS,
        });
      }

      const own = record && !expiredKeys.includes(this.queueRecordKey) ? record : null;
      const fromLegacy = legacyFresh ? this.toPendingList(legacy) : [];
      legacyCount = fromLegacy.length;
      const seen = new Set<string>();
      restored = [...fromLegacy, ...this.toPendingList(own)].filter((p) => {
        if (!p.updateId || seen.has(p.updateId)) return false;
        seen.add(p.updateId);
        return true;
      });
    } catch (error) {
      readFailed = true;
      console.warn(`[YJS-WS] ⚠️ Could not read the offline queue for room: ${this.roomId}`, error);
    }

    if (readFailed) {
      // We never learned what the record holds, so this provider does not own
      // it: writing would replace a copy we could not read, and the legacy
      // record is not ours to delete either. Queued edits still replay on the
      // next sync; they just do not become durable this session.
      return;
    }

    if (this.destroyed) {
      // Torn down while the record was being read. destroy() appended what it
      // held, from its own place in the chain; replacing the record from here
      // would undo edits made after that point by whatever replaced us.
      return;
    }
    this.queueRestored = true;

    try {
      // Whether the migrated entries reached disk. Nothing to copy counts as
      // copied; a write that did not land does not.
      let copied = true;
      {
        // Edits queued during the read are newer; they stay behind the restored ones.
        this.pendingUpdates = [...restored, ...this.pendingUpdates];
        if (restored.length > 0) {
          this.logTelemetry('yjs_pending_queue_restored', {
            count: restored.length,
            source: 'indexeddb',
            legacyCount,
          });
        }
        if (this.pendingUpdates.length > 0) {
          this.emitUnsaved();
          // Durable copy of the merged queue: the migrated legacy entries and
          // anything queued while persistence was still gated.
          copied = await this.persistQueuesNow();
        }
      }
      // Remove only the entries we actually migrated, once the v2 write has
      // landed. A still-open v1 tab may have appended to the shared record after
      // our snapshot; those survive and migrate on the next load (idempotent),
      // instead of being wiped by a blanket delete.
      if (legacy && (legacyCount === 0 || copied)) {
        await this.removeLegacyQueueRecord(migratedUpdateIds(legacy));
      } else if (legacy) {
        console.warn(`[YJS-WS] ⚠️ Keeping the legacy offline queue for room: ${this.roomId}; its copy did not persist`);
      }
    } catch {
      // best effort only
    }
  }

  /** Sweep expired records and read this tab's own. Runs inside the record chain. */
  private async readOwnQueueRecord(now: number): Promise<{ record: any; expiredKeys: string[] }> {
    const db = await this.openQueueDb();
    try {
      const tx = db.transaction(this.queueStoreName, 'readwrite');
      const store = tx.objectStore(this.queueStoreName);

      // Sweep records older than a day for any room or tab. A stale queue
      // replayed days later can overwrite a colleague's newer value: Yjs
      // resolves concurrent map writes by clientID, not by time.
      const expiredKeys = await new Promise<string[]>((resolve, reject) => {
        const keys: string[] = [];
        const req = store.openCursor();
        req.onsuccess = () => {
          const cursor = req.result;
          if (!cursor) { resolve(keys); return; }
          const rec = cursor.value as { key?: string; updatedAt?: number };
          if (typeof rec?.key === 'string' && !this.isQueueRecordFresh(rec, now)) {
            keys.push(rec.key);
          }
          cursor.continue();
        };
        req.onerror = () => reject(req.error);
      });
      for (const key of expiredKeys) store.delete(key);

      const record = await new Promise<any>((resolve, reject) => {
        const req = store.get(this.queueRecordKey);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
      await this.awaitTx(tx);
      return { record, expiredKeys };
    } finally {
      db.close();
    }
  }

  // ---- v1 offline queue (one record per room, keyPath roomId) ----------------

  /**
   * Never let a v1 access stall the restore: connect() waits on it, and updates
   * restored afterwards are only replayed by the next initial sync. On timeout
   * we simply skip the migration for this page load; nothing is deleted, so the
   * records are still there to migrate next time.
   */
  private withLegacyTimeout<T>(work: Promise<T>, fallback: T): Promise<T> {
    return new Promise<T>((resolve) => {
      let settled = false;
      const done = (value: T) => { if (!settled) { settled = true; resolve(value); } };
      const timer = setTimeout(() => {
        if (!settled) {
          console.warn(`[YJS-WS] ⚠️ Legacy offline-queue read timed out; migration deferred to the next load`);
        }
        done(fallback);
      }, this.LEGACY_READ_TIMEOUT_MS);
      work.then((value) => { clearTimeout(timer); done(value); })
        .catch(() => { clearTimeout(timer); done(fallback); });
    });
  }

  private openLegacyQueueDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = window.indexedDB.open(this.legacyQueueDbName);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  private dropLegacyQueueDb(): void {
    CustomWebsocketProvider.legacyQueueGone = true;
    try { window.indexedDB.deleteDatabase(this.legacyQueueDbName); } catch { /* ignore */ }
  }

  /**
   * Read every v1 record once per page and hand out copies from memory, so a
   * second room opened later needs no further v1 access.
   */
  private readAllLegacyQueueRecords(): Promise<Map<string, any>> {
    if (!CustomWebsocketProvider.legacyQueueRead) {
      CustomWebsocketProvider.legacyQueueRead = this.withLegacyTimeout(
        this.readAllLegacyQueueRecordsOnce(),
        new Map<string, any>(),
      );
    }
    return CustomWebsocketProvider.legacyQueueRead;
  }

  private async readAllLegacyQueueRecordsOnce(): Promise<Map<string, any>> {
    const empty = new Map<string, any>();
    try {
      const idb: any = window.indexedDB;
      if (typeof idb?.databases === 'function') {
        try {
          const dbs: Array<{ name?: string }> = await idb.databases();
          // Nothing to migrate: do not create the database just to look inside.
          if (!dbs.some((d) => d?.name === this.legacyQueueDbName)) return empty;
        } catch {
          // databases() unsupported or rejected; fall through to open()
        }
      }
      const db = await this.openLegacyQueueDb();
      if (!db.objectStoreNames.contains(this.queueStoreName)) {
        // open() just created an empty database because none existed.
        db.close();
        this.dropLegacyQueueDb();
        return empty;
      }
      const tx = db.transaction(this.queueStoreName, 'readonly');
      const records = await new Promise<any[]>((resolve, reject) => {
        const req = tx.objectStore(this.queueStoreName).getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
      });
      await this.awaitTx(tx);
      db.close();
      const byRoom = new Map<string, any>();
      records.forEach((rec) => {
        if (rec && typeof rec.roomId === 'string') byRoom.set(rec.roomId, rec);
      });
      return byRoom;
    } catch {
      return empty;
    }
  }

  private async readLegacyQueueRecord(): Promise<any | null> {
    if (CustomWebsocketProvider.legacyQueueGone) return null;
    const byRoom = await this.readAllLegacyQueueRecords();
    return byRoom.get(this.roomId) ?? null;
  }

  private async removeLegacyQueueRecord(migratedIds: Set<string>): Promise<void> {
    if (CustomWebsocketProvider.legacyQueueGone) return;
    try {
      const db = await this.openLegacyQueueDb();
      // Another room on this page may have dropped the database while we waited;
      // open() would then have handed us a freshly created empty shell.
      if (CustomWebsocketProvider.legacyQueueGone || !db.objectStoreNames.contains(this.queueStoreName)) {
        db.close();
        this.dropLegacyQueueDb();
        return;
      }
      // Read-modify-write in ONE transaction so a concurrent v1 append can't
      // interleave: re-read the CURRENT record (which may hold entries added
      // after our snapshot), remove only the ids we migrated, and write back the
      // survivors — deleting the record only when nothing is left. Never drop the
      // whole database: it is shared across rooms and may hold unmigrated appends.
      const tx = db.transaction(this.queueStoreName, 'readwrite');
      const store = tx.objectStore(this.queueStoreName);
      const current = await new Promise<any>((resolve, reject) => {
        const req = store.get(this.roomId);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
      if (current) {
        const { pending, unacked, empty } = pruneMigratedEntries(current, migratedIds);
        if (empty) {
          store.delete(this.roomId);
        } else {
          store.put({ ...current, pending, unacked });
        }
      }
      await this.awaitTx(tx);
      db.close();
    } catch {
      // best effort only: a surviving record is re-migrated (idempotent) or expires
    }
  }

  private awaitTx(tx: IDBTransaction): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  private openQueueDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = window.indexedDB.open(this.queueDbName, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(this.queueStoreName)) {
          db.createObjectStore(this.queueStoreName, { keyPath: 'key' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  public async logTelemetry(eventType: string, meta?: Record<string, any>): Promise<void> {
    try {
      if (typeof window === 'undefined') return;

      // Validate event type is in allowed list (Tightening #7)
      const allowedTypes = Object.values(TELEMETRY_EVENT_TYPES);
      if (!allowedTypes.includes(eventType as any)) {
        console.error(`[YJS-WS] ❌ Invalid telemetry event type: ${eventType} - not in frozen contract`);
        return;
      }

      let clientId = this.telemetry?.clientId;
      let sheetId = this.telemetry?.sheetId;
      if ((!clientId || !sheetId) && this.roomId.includes(':')) {
        const [roomClientId, roomSheetId] = this.roomId.split(':');
        clientId = clientId || roomClientId;
        sheetId = sheetId || roomSheetId;
      }

      if (!clientId || !sheetId) return;

      const body: Record<string, any> = {
        eventType,
        schemaVersion: TELEMETRY_SCHEMA_VERSION,  // Add schema version (Tightening #7)
        clientId,
        sheetId,
        roomId: this.roomId,
        userId: this.telemetry?.userId,
        userEmail: this.telemetry?.userEmail,
        tabId: this.tabId,
        sessionId: this.sessionId,
        clientTs: meta?.clientTs || new Date().toISOString(),
        eventSeq: ++this.eventSeq,
      };

      const topLevelKeys = [
        'updateId', 'origin', 'action', 'bytes', 'queueSize', 'count',
        'userEditCount', 'systemUpdateCount', 'syncDurationMs', 'stateBytes',
        'wasEmpty', 'code', 'reason', 'source', 'trigger', 'totalChanges',
        'cleared', 'rowId', 'field', 'beforeHash', 'afterHash', 'beforeLen',
        'afterLen', 'ageMs', 'chunked', 'chunkCount', 'pendingUpdatesCount',
        'ackLatencyMs', 'pendingCount', 'unackedCount', 'timeoutMs', 'serverTs',
        'retryCount', 'maxRetries',
      ];
      topLevelKeys.forEach((key) => {
        if (meta && meta[key] !== undefined) body[key] = meta[key];
      });

      const residualMeta: Record<string, any> = {};
      if (meta) {
        Object.keys(meta).forEach((key) => {
          if (key !== 'clientTs' && !topLevelKeys.includes(key)) {
            residualMeta[key] = meta[key];
          }
        });
      }
      if (Object.keys(residualMeta).length > 0) {
        body.meta = residualMeta;
      }

      await API.post('brief', '/briefs/telemetry/yjs', { body });

      if (this.telemetrySendFailures > 0 && eventType !== 'yjs_telemetry_delivery_summary') {
        const failedCount = this.telemetrySendFailures;
        this.telemetrySendFailures = 0;
        await API.post('brief', '/briefs/telemetry/yjs', {
          body: {
            eventType: 'yjs_telemetry_delivery_summary',
            clientId,
            sheetId,
            roomId: this.roomId,
            userId: this.telemetry?.userId,
            userEmail: this.telemetry?.userEmail,
            tabId: this.tabId,
            sessionId: this.sessionId,
            clientTs: new Date().toISOString(),
            eventSeq: ++this.eventSeq,
            failedSendCount: failedCount,
          },
        });
      }
    } catch (error) {
      this.telemetrySendFailures += 1;
      // Swallow telemetry errors to avoid impacting sync flow.
    }
  }
}
